import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import type {
	AfterViewInit,
	ElementRef,
	OnDestroy,
	TemplateRef,
} from '@angular/core';
import {
	ChangeDetectionStrategy,
	Component,
	CUSTOM_ELEMENTS_SCHEMA,
	inject,
	input,
	PLATFORM_ID,
	signal,
	viewChild,
	ViewEncapsulation,
} from '@angular/core';

import type { SwiperContainer } from 'swiper/element/bundle';
import { register } from 'swiper/element/bundle';
import type { SwiperOptions } from 'swiper/types';

import { cn } from '@valk-nx/core/lib/core';
import { SVG_ICON_IMPORTS } from '@valk-nx/helpers/lib/svg-unique-ids/svg-unique-ids.directive';
import {
	MOBILE,
	SMALL_DESKTOP,
	TABLET,
} from '@valk-nx/services/viewport/src/lib/determine-viewports';

// Module-scoped counter, incremented in the `uid` input default below to give
// each instance without an explicit uid a unique a11y id.
let uidCounter = 0;

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	imports: [NgTemplateOutlet, SVG_ICON_IMPORTS],
	selector: 'vp-slider',
	templateUrl: './slider.html',
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SliderComponent<T> implements AfterViewInit, OnDestroy {
	private readonly platformId = inject(PLATFORM_ID);

	readonly swiperRef = viewChild<ElementRef<SwiperContainer>>('swiper');
	readonly paginationRef = viewChild<ElementRef<HTMLElement>>('paginationEl');
	readonly prevButtonRef =
		viewChild<ElementRef<HTMLButtonElement>>('prevButton');
	readonly nextButtonRef =
		viewChild<ElementRef<HTMLButtonElement>>('nextButton');

	readonly items = input<T[]>([]);
	readonly itemTemplate = input.required<TemplateRef<{ $implicit: T }>>();
	readonly trackFn = input<(item: T) => unknown>((item) => item);
	readonly config = input<Partial<SwiperOptions>>({});
	readonly uid = input<string>(`auto-${++uidCounter}`);

	private swiper: SwiperContainer | null = null;

	readonly hasNav = signal(false);

	protected readonly cn = cn;

	private readonly baseConfig: SwiperOptions = {
		grabCursor: true,
		lazyPreloadPrevNext: 1,
		centerInsufficientSlides: true,
		spaceBetween: 4,
		loop: false,
		slidesPerView: 1.5,
		slidesOffsetBefore: 12,
		slidesOffsetAfter: 12,
		breakpoints: {
			[MOBILE]: { slidesPerView: 2.5 },
			[TABLET]: {
				slidesPerView: 3.5,
				slidesOffsetBefore: 0,
				slidesOffsetAfter: 0,
			},
			[SMALL_DESKTOP]: {
				slidesPerView: 4,
				slidesOffsetBefore: 0,
				slidesOffsetAfter: 0,
			},
		},
	};

	constructor() {
		register();
	}

	ngAfterViewInit() {
		this.initializeSwiper();
	}

	ngOnDestroy() {
		this.swiper?.swiper?.destroy(true, true);
		this.swiper = null;
	}

	initializeSwiper() {
		const host = this.swiperRef()?.nativeElement;
		if (!host || !isPlatformBrowser(this.platformId)) {
			return;
		}

		this.swiper = host;

		const mergedConfig: SwiperOptions = {
			...this.baseConfig,
			pagination: {
				el: this.paginationRef()?.nativeElement,
				clickable: true,
				bulletClass: 'size-1.5 bg-ui-175',
				bulletActiveClass: '!size-2 !bg-primary-100',
				lockClass: 'hidden',
				renderBullet: (_index, className) =>
					`<div class="${className} rounded-full cursor-pointer"></div>`,
			},
			navigation: {
				prevEl: this.prevButtonRef()?.nativeElement,
				nextEl: this.nextButtonRef()?.nativeElement,
			},
			a11y: { enabled: true, id: `slider-${this.uid()}` },
			keyboard: { enabled: true, onlyInViewport: true },
			...this.config(),
		};

		Object.assign(this.swiper, mergedConfig);
		this.swiper.classList.add('slider');
		this.swiper.initialize();

		const instance = this.swiper.swiper;
		this.hasNav.set(!(instance?.isLocked ?? true));
		instance?.on('lock', () => this.hasNav.set(false));
		instance?.on('unlock', () => this.hasNav.set(true));
	}
}
